import React, { useMemo } from 'react';

import Inventory2Icon from '@mui/icons-material/Inventory2';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';

import { formatSyncedMoney } from '../Assets/utils';

import AccountAssetName from './AccountAssetName';
import RelatedListCard from './RelatedListCard';
import { RELATED_PREVIEW_LIMIT, accountPath } from './accountRelatedModel';
import { useAccountAssets } from './useAccountAssets';
import { useAccountOfferId } from './useAccountOfferId';

const DESCRIPTION_SX = {
  display: '-webkit-box',
  overflow: 'hidden',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
} as const;

interface Props {
  accountId: string;
}

export default function AccountAssetsCard({ accountId }: Props) {
  const offerId = useAccountOfferId();
  const { assets, error, isLoading } = useAccountAssets(accountId);
  const preview = useMemo(() => assets.slice(0, RELATED_PREVIEW_LIMIT), [assets]);

  return (
    <RelatedListCard
      count={assets.length}
      error={error}
      icon={<Inventory2Icon color="action" />}
      isLoading={isLoading}
      title="Assets"
      viewAllTo={accountPath(accountId, offerId, '/assets')}
    >
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 200 }}>Asset Name</TableCell>
              <TableCell>Inclusion Description</TableCell>
              <TableCell>RRP</TableCell>
              <TableCell>Rack Rate</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {preview.map((asset) => (
              <TableRow key={asset.sfid} hover>
                <TableCell>
                  <AccountAssetName accountId={accountId} asset={asset} />
                </TableCell>
                <TableCell>
                  {asset.inclusion_description__c ? (
                    <Tooltip title={asset.inclusion_description__c}>
                      <Typography variant="body2" sx={DESCRIPTION_SX}>
                        {asset.inclusion_description__c}
                      </Typography>
                    </Tooltip>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>{formatSyncedMoney(asset.currencyisocode, asset.rrp__c) ?? '—'}</TableCell>
                <TableCell>{formatSyncedMoney(asset.currencyisocode, asset.rack_rate__c) ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </RelatedListCard>
  );
}
